import React, { useEffect } from 'react';
import { TDR, LANDING_PAGE_PLACEHOLDERS } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import { analytics, LINK_CLICKED_EVENT, MODAL_OPENED_EVENT } from '../../../../helpers/analytics';
import { setLang } from '../../../../helpers/setLang';
import { resolveLocalizedText } from '../../../../helpers/resolveLocalizedText';
import RestaurantEmailLink from '../../../buttons/RestaurantEmailLink';
import RestaurantPhoneLink from '../../../buttons/RestaurantPhoneLink';
import { ExternalLink } from '../../actions/external-link/external-link';
import { Logo } from '../../logo/logo';
import { Modal, ModalControlProps } from '../modal';

type SameDayBookingProps = {
	restaurant: TDR.Restaurant;
} & ModalControlProps;

export const SameDayBooking = ({ restaurant, open, onOpenChange }: SameDayBookingProps) => {
	const { t, i18n } = useTranslation();
	const altPlatform = restaurant.settings?.alternativePlatform?.vendor;
	const altPlatformDisplayName = TDR.RMSDisplayNameMap[altPlatform];
	const altPlatformHref = restaurant.settings?.alternativePlatform?.href;

	let href = altPlatformHref;
	if (i18n.language !== 'en') {
		href = setLang(href, i18n.language);
	}

	const logoSrc = `../../../../images/poweredBy/${altPlatform}.svg`;

	const contactByPhone = altPlatform === 'phone';
	const contactByEmail = altPlatform === 'email';
	const isCustom = altPlatform === 'custom';
	const useAltPlatform = !contactByPhone && !contactByEmail && !isCustom;

	const handleThirdPartyRMSClick = () => {
		analytics.track(LINK_CLICKED_EVENT, {
			href: href,
			purpose: 'Redirect to RMS from Same Day Booking Modal',
			platform: altPlatform
		});
	};

	const LogoIcon = !altPlatformDisplayName ? null : <Logo src={logoSrc} alt={altPlatformDisplayName} />;

	const getActionText = () => {
		if (contactByPhone && !!restaurant.phoneNumber) {
			return (
				<p>
					{t('checkout.modals.same_day_booking.action_phone')} <RestaurantPhoneLink phoneNumber={restaurant.phoneNumber} />.
				</p>
			);
		}

		if (contactByEmail && restaurant.contactEmail) {
			return (
				<p>
					{t('checkout.modals.same_day_booking.action_email')} <RestaurantEmailLink email={restaurant?.contactEmail} />.
				</p>
			);
		}

		if (isCustom) {
			return (
				<p>
					{t('checkout.modals.same_day_booking.action_platform')}
				</p>
			);
		}

		if (useAltPlatform) {
			return (
				<p>
					{t('checkout.modals.same_day_booking.action_platform')}
					{!altPlatformDisplayName ? (
						'.'
					) : (
						<>
							, {t('checkout.modals.same_day_booking.action_platform_with_name')} <strong>{altPlatformDisplayName}</strong>.
						</>
					)}
				</p>
			);
		}
	};

	useEffect(() => {
		if (open) {
			analytics.track(MODAL_OPENED_EVENT, { title: 'Same Day Booking Modal' });
		}
	}, [open]);

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<Modal.Header closeModal={() => onOpenChange(false)}>{t('checkout.modals.same_day_booking.title')}</Modal.Header>

			<Modal.Body>
				<p>
					{t('checkout.modals.same_day_booking.description')}
				</p>
				{getActionText()}
			</Modal.Body>

			<Modal.Footer>
				{isCustom ? (
					<ExternalLink
						fullWidth
						variant='secondary'
						href={href}
						aria-label={resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction}
						onClick={handleThirdPartyRMSClick}
					>
						{resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction}
					</ExternalLink>
				) : !altPlatformDisplayName || !useAltPlatform ? null : (
					<ExternalLink
						fullWidth
						variant='secondary'
						href={href}
						endIcon={LogoIcon}
						aria-label={`Find availability on ${altPlatformDisplayName}`}
						onClick={handleThirdPartyRMSClick}
					>
						{t('checkout.modals.same_day_booking.find_availability')}
					</ExternalLink>
				)}
			</Modal.Footer>
		</Modal>
	);
};
